import { useEffect, useMemo, useState } from "react";
import { Wallet, Flame, Snowflake, Clock, ArrowDown, ArrowUp, FileWarning, Users as UsersIcon, FileText, Network } from "lucide-react";
import AdminShell, { Panel, StatusPill, MockBadge, Btn } from "@/components/admin/AdminShell";
import KpiCard from "@/components/admin/KpiCard";
import { PrimaryTabs, SecondaryTabs } from "@/components/admin/SubTabs";
import DataTable from "@/components/admin/DataTable";
import AdminPager from "@/components/admin/AdminPager";
import { api } from "@/lib/api";
import { useAdminPager, unwrapAdminList } from "@/lib/useAdminPager";

const TABS = [
  { id: "assets", label: "Assets & Networks", icon: Network },
  { id: "overview", label: "Wallet Overview", icon: Wallet },
  { id: "deposits", label: "Deposits", icon: ArrowDown },
  { id: "withdrawals", label: "Withdrawals", icon: ArrowUp },
  { id: "sweep", label: "Deposit Sweep", icon: ArrowUp },
  { id: "hotcold", label: "Hot & Cold", icon: Flame },
  { id: "amlfiu", label: "AML & FIU", icon: FileWarning },
  { id: "userwallets", label: "User Wallets", icon: UsersIcon },
  { id: "audit", label: "Audit Logs", icon: FileText },
];

function useLiveUsdPrices() {
  const [prices, setPrices] = useState({ USDT: 1, USDC: 1 });
  useEffect(() => {
    api
      .get("/api/markets/list", { params: { quote: "USDT", category: "all", page_size: 50 } })
      .then(({ data }) => {
        const next = { USDT: 1, USDC: 1 };
        (data?.rows ?? []).forEach((r) => {
          if (r.symbol && Number.isFinite(r.price)) next[r.symbol] = r.price;
        });
        setPrices(next);
      })
      .catch(() => {});
  }, []);
  return prices;
}

export default function WalletManagement() {
  const [tab, setTab] = useState("assets");
  const [assets, setAssets] = useState([]);
  const [kpis, setKpis] = useState({ pending_withdrawals: 0, deposits_today: 0, withdrawals_today: 0 });
  const prices = useLiveUsdPrices();

  useEffect(() => {
    api.get("/api/admin/wallet/assets").then(({ data }) => setAssets(data)).catch(() => {});
    api.get("/api/admin/wallet/kpis").then(({ data }) => setKpis(data)).catch(() => {});
  }, []);

  const total = useMemo(
    () => assets.reduce((s, a) => s + (a.total_balance || 0) * (prices[a.symbol] || 0), 0),
    [assets, prices],
  );
  const hot = useMemo(
    () => assets.reduce((s, a) => s + (a.hot_balance || 0) * (prices[a.symbol] || 0), 0),
    [assets, prices],
  );
  const cold = total - hot;

  return (
    <AdminShell title="Wallets" subtitle="Hot and cold balances, user wallets and ledger adjustments.">
      <div className="mb-5"><MockBadge phase="B" /></div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <KpiCard label="Total Assets" value={`$${(total / 1e6).toFixed(2)}M`} icon={Wallet} variant="yellow" />
        <KpiCard label="Hot Wallet" value={`$${(hot / 1e6).toFixed(2)}M`} delta={total ? `${((hot / total) * 100).toFixed(1)}%` : "—"} deltaLabel="of total" icon={Flame} variant="red" />
        <KpiCard label="Cold Wallet" value={`$${(cold / 1e6).toFixed(2)}M`} delta={total ? `${((cold / total) * 100).toFixed(1)}%` : "—"} deltaLabel="of total" icon={Snowflake} variant="blue" />
        <KpiCard label="Pending Withdrawals" value={String(kpis.pending_withdrawals ?? 0)} sub={`${kpis.deposits_today ?? 0} deposits today`} icon={Clock} variant="purple" />
      </div>

      <PrimaryTabs tabs={TABS} value={tab} onChange={setTab} testIdPrefix="wallet-tab" />
      <div className="mt-5">
        {tab === "assets" && <AssetsTab assets={assets} prices={prices} />}
        {tab === "overview" && <OverviewTab assets={assets} prices={prices} />}
        {tab === "deposits" && <DepositsTab />}
        {tab === "withdrawals" && <WithdrawalsTab />}
        {tab === "sweep" && <DepositSweepTab />}
        {tab === "hotcold" && <HotColdTab assets={assets} />}
        {tab === "amlfiu" && <AmlFiuTab />}
        {tab === "userwallets" && <UserWalletsTab />}
        {tab === "audit" && <AuditTab />}
      </div>
    </AdminShell>
  );
}

const ASSET_SUBTABS = [{ id: "all", label: "All Assets" }, { id: "networks", label: "Network Configuration" }];

function AssetsTab({ assets, prices }) {
  const [sub, setSub] = useState("all");
  return (
    <div>
      <SecondaryTabs tabs={ASSET_SUBTABS} value={sub} onChange={setSub} testIdPrefix="assets-sub" />
      {sub === "all" && (
        <Panel className="mt-4">
          <DataTable
            testid="assets-table"
            columns={[
              { key: "asset", header: "Asset", render: (a) => <div><div className="font-semibold">{a.symbol}</div><div className="text-[11px] text-muted-foreground">{a.name}</div></div> },
              { key: "decimals", header: "Decimals", render: (a) => a.decimals },
              { key: "networks", header: "Networks", render: (a) => <div className="flex flex-wrap gap-1">{a.networks?.map((n) => <span key={n} className="text-[10px] bg-surface-2 px-2 py-0.5 rounded text-muted-foreground">{n}</span>)}</div> },
              { key: "total", header: "Total Balance", align: "right", render: (a) => <div><div className="font-semibold tabular-nums">{(a.total_balance || 0).toLocaleString()}</div><div className="text-[10px] text-muted-foreground">${((a.total_balance || 0) * (prices[a.symbol] || 0)).toLocaleString(undefined, { maximumFractionDigits: 0 })}</div></div> },
              { key: "min_dep", header: "Min Deposit", align: "right", render: (a) => a.min_deposit },
              { key: "max_with", header: "Max Withdraw", align: "right", render: (a) => a.max_withdraw?.toLocaleString() },
              { key: "status", header: "Status", render: (a) => <StatusPill value={a.status} /> },
            ]}
            rows={assets}
            rowKey={(a) => a.id}
          />
        </Panel>
      )}
      {sub === "networks" && (
        <Panel className="mt-4">
          <DataTable
            columns={[
              { key: "asset", header: "Asset" },
              { key: "network", header: "Network" },
              { key: "min_conf", header: "Min Confirmations", align: "right" },
              { key: "fee", header: "Network Fee", align: "right" },
              { key: "status", header: "Status", render: (r) => <StatusPill value={r.status} /> },
            ]}
            rows={assets.flatMap((a) => (a.networks || []).map((n) => ({ asset: a.symbol, network: n, min_conf: 3, fee: "0.0001", status: "active" })))}
          />
        </Panel>
      )}
    </div>
  );
}

function OverviewTab({ assets, prices }) {
  return (
    <div className="grid grid-cols-3 gap-4">
      {assets.map((a) => (
        <Panel key={a.id} className="p-5">
          <div className="flex justify-between items-start mb-3">
            <div><div className="font-semibold text-[16px]">{a.symbol}</div><div className="text-[11px] text-muted-foreground">{a.name}</div></div>
            <StatusPill value={a.status} />
          </div>
          <div className="text-[24px] font-semibold tabular-nums">{(a.total_balance || 0).toLocaleString()}</div>
          <div className="text-[12px] text-muted-foreground mt-1">≈ ${((a.total_balance || 0) * (prices[a.symbol] || 0)).toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
          <div className="grid grid-cols-2 gap-2 mt-4 text-[11px]">
            <div className="p-2 bg-background rounded"><span className="text-down">🔥 Hot</span> <div className="font-semibold tabular-nums">{(a.hot_balance || 0).toLocaleString()}</div></div>
            <div className="p-2 bg-background rounded"><span className="text-primary">❄ Cold</span> <div className="font-semibold tabular-nums">{(a.cold_balance || 0).toLocaleString()}</div></div>
          </div>
        </Panel>
      ))}
    </div>
  );
}

const DEPOSIT_SUBTABS = [
  { id: "events", label: "On-chain events" },
  { id: "ledger", label: "Ledger credits" },
];

function DepositsTab() {
  const [sub, setSub] = useState("events");
  return (
    <div data-testid="admin-deposits">
      <SecondaryTabs tabs={DEPOSIT_SUBTABS} value={sub} onChange={setSub} testIdPrefix="deposits-sub" />
      <div className="mt-4">
        {sub === "events" ? <DepositEventsPanel /> : <DepositLedgerPanel />}
      </div>
    </div>
  );
}

function DepositEventsPanel() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [txHash, setTxHash] = useState("");
  const [busyId, setBusyId] = useState("");
  const [msg, setMsg] = useState("");
  const pager = useAdminPager();

  const load = async () => {
    setLoading(true);
    try {
      const params = { skip: pager.skip, limit: pager.limit };
      if (status) params.status = status;
      if (txHash.trim()) params.tx_hash = txHash.trim();
      const { data } = await api.get("/api/admin/wallet/deposit-events", { params });
      setRows(data.events || []);
      setTotal(data.total || 0);
      pager.setTotal(data.total || 0);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [pager.page, status]);

  const credit = async (id) => {
    if (!window.confirm("Credit this on-chain deposit to the user ledger now?")) return;
    setBusyId(id);
    setMsg("");
    try {
      await api.post(`/api/admin/wallet/deposit-events/${id}/credit`);
      setMsg("Credited.");
      await load();
    } catch (e) {
      setMsg(e.response?.data?.detail || "Credit failed");
    } finally {
      setBusyId("");
    }
  };

  const canCredit = (r) => ["pending", "confirming", "below_min", "pending_kyc"].includes(r.status);

  return (
    <div className="space-y-3">
      <Panel className="p-4 flex flex-wrap gap-2 items-end">
        <div>
          <label className="text-[11px] text-muted-foreground">Status</label>
          <select value={status} onChange={(e) => { pager.setPage(1); setStatus(e.target.value); }} className="block h-[36px] px-2 bg-background border border-line rounded-md text-[13px]">
            <option value="">All</option>
            <option value="pending">pending</option>
            <option value="confirming">confirming</option>
            <option value="credited">credited</option>
            <option value="orphan">orphan</option>
            <option value="reorg_review">reorg_review</option>
          </select>
        </div>
        <div>
          <label className="text-[11px] text-muted-foreground">Tx hash</label>
          <input value={txHash} onChange={(e) => setTxHash(e.target.value)} placeholder="0x…" className="block h-[36px] px-2 bg-background border border-line rounded-md text-[13px] font-mono w-[280px]" />
        </div>
        <Btn onClick={() => { pager.setPage(1); load(); }}>Search</Btn>
        {msg && <span className="text-[12px] text-primary">{msg}</span>}
        <span className="text-[11px] text-muted-foreground ml-auto">Sightings from QuickNode poller · {total} total</span>
      </Panel>
      <Panel>
        {loading ? (
          <div className="p-8 text-center text-muted-foreground text-[13px]">Loading events…</div>
        ) : (
          <>
            <DataTable
              testid="deposit-events-table"
              columns={[
                { key: "id", header: "Event", render: (r) => <span className="font-mono text-[11px]">{r.id}</span> },
                { key: "user_id", header: "User" },
                { key: "asset", header: "Asset", render: (r) => <span className="font-semibold">{r.asset}</span> },
                { key: "amount", header: "Amount", align: "right", render: (r) => `${r.amount} ${r.asset}` },
                { key: "tx_hash", header: "Tx", render: (r) => <span className="font-mono text-[11px]">{r.tx_hash ? `${String(r.tx_hash).slice(0, 12)}…` : "—"}</span> },
                { key: "confirmations", header: "Confs", align: "right" },
                { key: "sweep_status", header: "Sweep", render: (r) => r.sweep_status || "—" },
                { key: "status", header: "Status", render: (r) => <StatusPill value={r.status} /> },
                {
                  key: "actions",
                  header: "",
                  render: (r) => canCredit(r) ? (
                    <Btn onClick={() => credit(r.id)} disabled={busyId === r.id} data-testid={`credit-event-${r.id}`}>
                      {busyId === r.id ? "…" : "Credit"}
                    </Btn>
                  ) : null,
                },
              ]}
              rows={rows}
              rowKey={(r) => r.id}
              empty="No on-chain deposit events yet. Enable DEPOSIT_POLL_ENABLED and wait for transfers."
            />
            <AdminPager page={pager.page} total={pager.total} pageSize={pager.pageSize} onPage={pager.setPage} testId="deposit-events-pager" />
          </>
        )}
      </Panel>
    </div>
  );
}

function DepositLedgerPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const pager = useAdminPager();
  useEffect(() => {
    setLoading(true);
    api
      .get("/api/admin/wallet/deposits", { params: { skip: pager.skip, limit: pager.limit } })
      .then(({ data }) => {
        const { items, total } = unwrapAdminList(data, "deposits");
        setRows(items);
        pager.setTotal(total);
      })
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pager.page]);
  if (loading) return <Panel className="p-8 text-center text-muted-foreground">Loading ledger…</Panel>;
  return (
    <Panel>
      <DataTable
        columns={[
          { key: "tx_id", header: "Deposit ID", render: (r) => <span className="font-mono text-[11px]">{r.tx_id}</span> },
          { key: "user_email", header: "User", render: (r) => r.user_email || r.user_id },
          { key: "asset", header: "Asset", render: (r) => <span className="font-semibold">{r.asset}</span> },
          { key: "network", header: "Network" },
          { key: "amount", header: "Amount", align: "right", render: (r) => `${r.amount} ${r.asset}` },
          { key: "confirmations", header: "Confs", align: "right", render: (r) => `${r.confirmations ?? 0}/${r.required_confirmations ?? "—"}` },
          { key: "balance_status", header: "Balance", render: (r) => r.balance_status || "—" },
          { key: "status", header: "Status", render: (r) => <StatusPill value={r.status} /> },
          { key: "created_at", header: "When", render: (r) => r.created_at ? new Date(r.created_at).toLocaleString() : "—" },
        ]}
        rows={rows}
        rowKey={(r) => r.tx_id}
        empty="No deposits in ledger yet."
      />
      <AdminPager page={pager.page} total={pager.total} pageSize={pager.pageSize} onPage={pager.setPage} testId="wallet-deposits-pager" />
    </Panel>
  );
}

function WithdrawalsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const pager = useAdminPager();
  useEffect(() => {
    setLoading(true);
    api
      .get("/api/admin/wallet/withdrawals", { params: { skip: pager.skip, limit: pager.limit } })
      .then(({ data }) => {
        const { items, total } = unwrapAdminList(data, "withdrawals");
        setRows(items);
        pager.setTotal(total);
      })
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pager.page]);
  if (loading) return <Panel className="p-8 text-center text-muted-foreground">Loading withdrawals…</Panel>;
  return (
    <Panel>
      <DataTable
        columns={[
          { key: "tx_id", header: "Withdrawal ID", render: (r) => <span className="font-mono text-[11px]">{r.tx_id}</span> },
          { key: "user_id", header: "User", render: (r) => r.user_email || r.user_id },
          { key: "asset", header: "Asset", render: (r) => <span className="font-semibold">{r.asset}</span> },
          { key: "network", header: "Network" },
          { key: "amount", header: "Amount", align: "right", render: (r) => `${r.amount} ${r.asset}` },
          { key: "to_address", header: "Destination", render: (r) => <span className="font-mono text-[11px]">{r.to_address ? `${String(r.to_address).slice(0, 14)}…` : "—"}</span> },
          { key: "status", header: "Status", render: (r) => <StatusPill value={r.status} /> },
          { key: "created_at", header: "When", render: (r) => r.created_at ? new Date(r.created_at).toLocaleString() : "—" },
        ]}
        rows={rows}
        rowKey={(r) => r.tx_id}
        empty="No withdrawal requests yet."
      />
      <AdminPager page={pager.page} total={pager.total} pageSize={pager.pageSize} onPage={pager.setPage} testId="wallet-withdrawals-pager" />
    </Panel>
  );
}

function HotColdTab({ assets }) {
  return (
    <div className="space-y-4">
      <HotWalletConfig />
      <Panel>
      <DataTable
        columns={[
          { key: "asset", header: "Asset", render: (a) => <span className="font-semibold">{a.symbol}</span> },
          { key: "hot", header: "Hot Balance", align: "right", render: (a) => <span className="text-down">{(a.hot_balance || 0).toLocaleString()}</span> },
          { key: "cold", header: "Cold Balance", align: "right", render: (a) => <span className="text-primary">{(a.cold_balance || 0).toLocaleString()}</span> },
          { key: "ratio", header: "Hot %", align: "right", render: (a) => (a.total_balance ? `${((a.hot_balance / a.total_balance) * 100).toFixed(1)}%` : "—") },
          { key: "actions", header: "Actions", align: "right", render: () => <button type="button" className="text-primary text-[11px] font-semibold hover:underline">Rebalance</button> },
        ]}
        rows={assets}
        rowKey={(a) => a.id}
      />
    </Panel>
    </div>
  );
}

function HotWalletConfig() {
  const [status, setStatus] = useState(null);
  const [mnemonic, setMnemonic] = useState("");
  const [passphrase, setPassphrase] = useState("");
  const [privateKey, setPrivateKey] = useState("");
  const [showMnemonic, setShowMnemonic] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/api/admin/wallet/hot-wallet/config");
      setStatus(data);
    } catch (e) {
      setMsg(e.response?.data?.detail || "Could not load hot wallet configuration");
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!mnemonic.trim() && !privateKey.trim()) {
      setMsg("Enter a mnemonic, a private key, or both.");
      return;
    }
    if (!window.confirm("Save these secrets on the server? They are encrypted and used when .env is empty. A saved mnemonic derives deposit addresses. A saved private key is the hot wallet.")) return;
    setBusy(true);
    setMsg("");
    try {
      const body = {};
      if (mnemonic.trim()) {
        body.mnemonic = mnemonic.trim();
        body.passphrase = passphrase;
      }
      if (privateKey.trim()) body.private_key = privateKey.trim();
      const { data } = await api.put("/api/admin/wallet/hot-wallet/config", body);
      setStatus(data);
      setMnemonic("");
      setPassphrase("");
      setPrivateKey("");
      setShowMnemonic(false);
      setShowKey(false);
      setMsg("Saved. The values are encrypted and are not shown again.");
    } catch (e) {
      setMsg(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const clearField = async (which) => {
    const label = which === "mnemonic" ? "stored mnemonic" : "stored hot private key";
    if (!window.confirm(`Remove the ${label}? Env values will be used if they are set.`)) return;
    setBusy(true);
    setMsg("");
    try {
      const body = which === "mnemonic" ? { clear_mnemonic: true } : { clear_private_key: true };
      const { data } = await api.put("/api/admin/wallet/hot-wallet/config", body);
      setStatus(data);
      setMsg(`Removed the ${label}.`);
    } catch (e) {
      setMsg(e.response?.data?.detail || "Clear failed");
    } finally {
      setBusy(false);
    }
  };

  const sourceLabel = (source) => (source === "database" ? "saved on server" : source === "env" ? ".env" : "not set");

  return (
    <Panel className="p-5 space-y-3 max-w-[900px]" data-testid="hot-wallet-config">
      <h3 className="text-[14px] font-semibold">Hot wallet configuration</h3>
      <p className="text-[12px] text-muted-foreground">
        Save a mnemonic and a hot-wallet private key here. They are encrypted on the server and used when the matching .env value is empty.
        If a mnemonic is saved, deposit addresses are derived from it. If a private key is saved, that key is the treasury hot wallet.
        Leave a field blank to keep the current value. Secrets are not shown after save.
      </p>
      {status && (
        <div className="text-[12px] space-y-1">
          <div>Mnemonic: {status.mnemonic_configured ? sourceLabel(status.mnemonic_source) : "not set"}{status.passphrase_configured ? " · passphrase set" : ""}</div>
          <div>Hot wallet key: {status.hot_key_configured ? sourceLabel(status.hot_key_source) : "not set"}</div>
          <div>Hot address: <span className="font-mono">{status.hot_address || "—"}</span></div>
          {status.updated_at && (
            <div className="text-muted-foreground">Last change {new Date(status.updated_at).toLocaleString()}{status.updated_by ? ` by ${status.updated_by}` : ""}</div>
          )}
        </div>
      )}
      <label className="block text-[12px] space-y-1">
        <span className="text-muted-foreground">Mnemonic (12–24 words)</span>
        <input
          type={showMnemonic ? "text" : "password"}
          value={mnemonic}
          onChange={(e) => setMnemonic(e.target.value)}
          autoComplete="off"
          spellCheck={false}
          placeholder="Leave blank to keep the saved mnemonic"
          data-testid="hot-wallet-mnemonic"
          className="w-full h-[40px] px-3 rounded-md border bg-background text-[13px] font-mono outline-none"
        />
      </label>
      <button type="button" className="text-[11px] text-primary font-semibold" onClick={() => setShowMnemonic((v) => !v)}>
        {showMnemonic ? "Hide mnemonic" : "Show mnemonic"}
      </button>
      <label className="block text-[12px] space-y-1">
        <span className="text-muted-foreground">Mnemonic passphrase (optional)</span>
        <input
          type="password"
          value={passphrase}
          onChange={(e) => setPassphrase(e.target.value)}
          autoComplete="new-password"
          placeholder="Only used when you also enter a mnemonic"
          className="w-full h-[40px] px-3 rounded-md border bg-background text-[13px] outline-none"
        />
      </label>
      <label className="block text-[12px] space-y-1">
        <span className="text-muted-foreground">Hot wallet private key</span>
        <input
          type={showKey ? "text" : "password"}
          value={privateKey}
          onChange={(e) => setPrivateKey(e.target.value)}
          autoComplete="new-password"
          spellCheck={false}
          placeholder="64 hex characters, with or without 0x"
          data-testid="hot-wallet-private-key"
          className="w-full h-[40px] px-3 rounded-md border bg-background text-[13px] font-mono outline-none"
        />
      </label>
      <button type="button" className="text-[11px] text-primary font-semibold" onClick={() => setShowKey((v) => !v)}>
        {showKey ? "Hide private key" : "Show private key"}
      </button>
      <div className="flex flex-wrap gap-2 items-center">
        <Btn onClick={save} disabled={busy} data-testid="hot-wallet-save">Save</Btn>
        <Btn kind="ghost" onClick={() => clearField("mnemonic")} disabled={busy || !status?.mnemonic_configured || status?.mnemonic_source !== "database"}>
          Clear saved mnemonic
        </Btn>
        <Btn kind="ghost" onClick={() => clearField("private_key")} disabled={busy || !status?.hot_key_configured || status?.hot_key_source !== "database"}>
          Clear saved private key
        </Btn>
      </div>
      {msg && <div className="text-[12px] text-primary">{msg}</div>}
    </Panel>
  );
}

function AmlFiuTab() {
  return <Panel className="p-10 text-center text-muted-foreground text-[13px]">Wallet-level AML hooks — see the <a href="/admin/aml" className="text-primary hover:underline">FIU/AML module</a> for the full alert queue.</Panel>;
}

function UserWalletsTab() {
  return <Panel className="p-10 text-center text-muted-foreground text-[13px]">Per-user wallet drilldown — accessed via <a href="/admin/users" className="text-primary hover:underline">User Management → User Detail → Wallets tab</a>.</Panel>;
}

function DepositSweepTab() {
  const [live, setLive] = useState(null);
  const [items, setItems] = useState([]);
  const [runs, setRuns] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [confirmLive, setConfirmLive] = useState(false);

  const refresh = async () => {
    try {
      const [s, p, h] = await Promise.all([
        api.get("/api/admin/wallet/deposit-sweeps/live-status"),
        api.post("/api/admin/wallet/deposit-sweeps/preview"),
        api.get("/api/admin/wallet/deposit-sweeps/history"),
      ]);
      setLive(s.data);
      setItems(p.data.items || []);
      setRuns(h.data.runs || []);
    } catch (e) {
      setMsg(e.response?.data?.detail || e.message || "Failed to load sweep status");
    }
  };

  useEffect(() => { refresh(); }, []);

  const toggleLive = async () => {
    if (!live) return;
    const next = !live.admin_enabled;
    if (next && !window.confirm("Enable LIVE deposit sweeps? This allows broadcasting real on-chain txs when you run with confirm_live.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/api/admin/wallet/deposit-sweeps/live-enabled", { enabled: next });
      setLive(data);
      setMsg(next ? "Live sweeps enabled (admin)." : "Live sweeps disabled.");
    } catch (e) {
      setMsg(e.response?.data?.detail || "Toggle failed");
    } finally {
      setBusy(false);
    }
  };

  const run = async (dry) => {
    if (!dry && !confirmLive) {
      setMsg("Check “Confirm live broadcast” to run a live sweep.");
      return;
    }
    if (!dry && !window.confirm("Broadcast real HD→hot sweep transactions now?")) return;
    setBusy(true);
    setMsg("");
    try {
      const { data } = await api.post("/api/admin/wallet/deposit-sweeps/run", {
        dry_run: dry,
        confirm_live: !dry && confirmLive,
      });
      setMsg(dry ? `Dry-run ${data.run_id}: ${data.summary?.total || 0} items` : `Live run ${data.run_id}`);
      await refresh();
    } catch (e) {
      setMsg(e.response?.data?.detail || "Sweep failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4 max-w-[900px]" data-testid="deposit-sweep-panel">
      <Panel className="p-5 space-y-3">
        <h3 className="text-[14px] font-semibold">HD → hot wallet sweep</h3>
        <p className="text-[12px] text-muted-foreground">
          Moves funds from per-user HD deposit addresses into the treasury hot wallet (QuickNode / BSC).
          Dry-run is always safe. Live requires the admin toggle (or server env) plus confirm below.
        </p>
        {live && (
          <div className="text-[12px] space-y-1">
            <div>Hot wallet: <span className="font-mono">{live.hot_wallet || "— not configured"}</span>{live.hot_key_source ? ` (${live.hot_key_source})` : ""}</div>
            <div>Mnemonic: {live.mnemonic_configured ? `configured (${live.mnemonic_source || "env"})` : "not configured"}</div>
            <div>RPC: {live.rpc_configured ? "ok" : "missing"} · Live allowed: {live.live_allowed ? "yes" : "no"}</div>
            <div>Admin toggle: {live.admin_enabled ? "ON" : "OFF"} · Env: {live.env_enabled ? "ON" : "OFF"}</div>
          </div>
        )}
        <div className="flex flex-wrap gap-2 items-center">
          <Btn onClick={toggleLive} disabled={busy || !live} data-testid="sweep-live-toggle">
            {live?.admin_enabled ? "Disable live sweeps" : "Enable live sweeps"}
          </Btn>
          <Btn onClick={() => run(true)} disabled={busy} data-testid="sweep-dry-run">Dry-run</Btn>
          <Btn onClick={() => run(false)} disabled={busy || !live?.live_allowed} kind="danger" data-testid="sweep-live-run">
            Run live
          </Btn>
          <label className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
            <input type="checkbox" checked={confirmLive} onChange={(e) => setConfirmLive(e.target.checked)} />
            Confirm live broadcast
          </label>
          <Btn onClick={refresh} disabled={busy}>Refresh</Btn>
        </div>
        {msg && <div className="text-[12px] text-primary">{msg}</div>}
      </Panel>

      <Panel className="p-5">
        <h3 className="text-[14px] font-semibold mb-3">Preview ({items.length})</h3>
        <DataTable
          columns={[
            { key: "address", header: "Address", render: (r) => <span className="font-mono text-[11px]">{r.address?.slice(0, 10)}…</span> },
            { key: "user_id", header: "User" },
            { key: "bnb", header: "BNB", render: (r) => Number(r.bnb || 0).toFixed(6) },
            { key: "usdt", header: "USDT", render: (r) => Number(r.usdt || 0).toFixed(4) },
            { key: "sweepable", header: "Ready", render: (r) => (r.sweepable ? "yes" : "no") },
          ]}
          rows={items}
          rowKey={(r) => r.address}
          empty="No HD balances to sweep."
        />
      </Panel>

      <Panel className="p-5">
        <h3 className="text-[14px] font-semibold mb-3">Recent runs</h3>
        <DataTable
          columns={[
            { key: "id", header: "Run" },
            { key: "created_at", header: "When" },
            { key: "dry_run", header: "Mode", render: (r) => (r.dry_run ? "dry-run" : "live") },
            { key: "summary", header: "Items", render: (r) => r.summary?.total ?? "—" },
          ]}
          rows={runs}
          rowKey={(r) => r.id}
          empty="No sweep runs yet."
        />
      </Panel>
    </div>
  );
}

function AuditTab() {
  const [logs, setLogs] = useState([]);
  useEffect(() => {
    api
      .get("/api/admin/audit-log", { params: { limit: 30 } })
      .then(({ data }) => setLogs(Array.isArray(data) ? data : data?.logs ?? []))
      .catch(() => setLogs([]));
  }, []);
  return (
    <Panel>
      <DataTable
        columns={[
          { key: "at", header: "When", render: (r) => r.at || r.created_at },
          { key: "actor", header: "Admin", render: (r) => r.actor || r.actor_email },
          { key: "action", header: "Action" },
          { key: "target_id", header: "Target", render: (r) => r.target_id || r.target },
        ]}
        rows={logs}
        rowKey={(r, i) => r.target_id || i}
      />
    </Panel>
  );
}
